import { Slot } from "@radix-ui/react-slot";
import type { ButtonHTMLAttributes } from "react";

import { cn } from "@/ui/cn";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

const VARIANT: Record<ButtonVariant, string> = {
  primary: "bg-accent text-accent-ink shadow-card hover:bg-accent-hover disabled:hover:bg-accent",
  secondary:
    "bg-surface text-ink border border-line-strong shadow-card hover:bg-sunken disabled:hover:bg-surface",
  ghost: "text-ink-2 hover:bg-sunken hover:text-ink",
  danger: "bg-danger text-white shadow-card hover:opacity-90",
};

const SIZE: Record<ButtonSize, string> = {
  sm: "h-7 px-2.5 text-[13px] gap-1.5 rounded-md",
  md: "h-9 px-3.5 text-sm gap-2 rounded-lg",
  lg: "h-11 px-5 text-[15px] gap-2 rounded-lg",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Render the child (a Link, an anchor) with the button's look. */
  asChild?: boolean;
}

export function buttonClass(variant: ButtonVariant = "secondary", size: ButtonSize = "md"): string {
  return cn(
    "inline-flex shrink-0 items-center justify-center font-medium whitespace-nowrap no-underline",
    "transition-colors disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:size-4",
    VARIANT[variant],
    SIZE[size],
  );
}

export function Button({
  variant = "secondary",
  size = "md",
  asChild = false,
  className,
  type,
  ...rest
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      {...(asChild ? {} : { type: type ?? "button" })}
      className={cn(buttonClass(variant, size), className)}
      {...rest}
    />
  );
}
